import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCms } from '../contexts/CmsContext';
import Header from './Header';
import Footer from './Footer';
import ScrollAnimation from './ScrollAnimation';
import { FaShieldAlt, FaHandshake } from 'react-icons/fa';
import {
  MdHighQuality,
  MdVisibility,
  MdRocketLaunch,
  MdFormatQuote,
  MdPublic,
  MdLocalShipping,
  MdEngineering
} from 'react-icons/md';
import SaudiVisionLogo from '../assests/Saudi_Vision_2030_logo.svg.png';

const About = () => {
  const { language } = useLanguage();
  const { getContent } = useCms();
  const t = getContent(language);
  const a = t.aboutPage;
  const isRTL = language === 'ar';

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`${
        isRTL ? 'font-cairo' : 'font-sans'
      } bg-white text-slate-900 overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'} animate-fade-in`}
    >
      <Header />

      <main>
        <section className="relative h-[60vh] min-h-[450px] w-full flex items-center overflow-hidden pt-16">
          <div className="absolute inset-0 z-0">
            <img
              alt="Saudi Industrial Infrastructure"
              className="w-full h-full object-cover scale-105"
              src={a.hero.bgImage}
            />
            <div className="absolute inset-0 hero-light-overlay" />
          </div>

          <div className="relative z-10 max-w-[1600px] mx-auto px-8 lg:px-16 w-full">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px w-12 bg-secondary" />
                <span className="text-secondary text-[10px] font-bold tracking-[0.3em] uppercase">
                  {a.hero.badge}
                </span>
              </div>
              <h1 className="text-primary text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-[0.9] tracking-tight">
                {a.hero.titleLine1}{' '}
                <span className="text-secondary">{a.hero.titleEmphasis}</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-800/80 max-w-2xl font-light leading-relaxed tracking-wide">
                {a.hero.subtitle}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-surface py-16 border-b border-gray-100">
          <div className="max-w-[1600px] mx-auto px-8 lg:px-16">
            <ScrollAnimation animation="fade-up" className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="flex items-center gap-6 group cursor-default">
                <FaShieldAlt className="text-4xl text-primary/50 group-hover:text-primary transition-colors" />
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500/70 mb-1">
                    {a.pillars.integrity.label}
                  </h4>
                  <p className={`text-lg ${isRTL ? 'font-cairo' : ''} text-slate-900`}>
                    {a.pillars.integrity.title}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6 group cursor-default">
                <MdHighQuality className="text-4xl text-primary/50 group-hover:text-primary transition-colors" />
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500/70 mb-1">
                    {a.pillars.quality.label}
                  </h4>
                  <p className={`text-lg ${isRTL ? 'font-cairo' : ''} text-slate-900`}>
                    {a.pillars.quality.title}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6 group cursor-default">
                <FaHandshake className="text-4xl text-primary/50 group-hover:text-primary transition-colors" />
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500/70 mb-1">
                    {a.pillars.commitment.label}
                  </h4>
                  <p className={`text-lg ${isRTL ? 'font-cairo' : ''} text-slate-900`}>
                    {a.pillars.commitment.title}
                  </p>
                </div>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-32 bg-white relative">
          <div className="max-w-[1600px] mx-auto px-8 lg:px-16">
            <ScrollAnimation animation="fade-up" className="grid md:grid-cols-2 gap-px bg-gray-100">
              <div className="bg-surface p-10 lg:p-16 space-y-8">
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-8">
                  <MdVisibility className="text-secondary text-4xl" />
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-primary">
                  {a.visionMission.vision.title}
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed font-light">
                  {a.visionMission.vision.text}
                </p>
              </div>

              <div className="bg-surface p-10 lg:p-16 space-y-8 border-l border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-8">
                  <MdRocketLaunch className="text-accent text-4xl" />
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-primary">
                  {a.visionMission.mission.title}
                </h2>
                <p className="text-lg text-slate-700 leading-relaxed font-light">
                  {a.visionMission.mission.text}
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-40 bg-surface relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 -skew-x-12 translate-x-32" />
          <div className="max-w-[1600px] mx-auto px-8 lg:px-16 relative z-10">
            <ScrollAnimation animation="fade-left" className="bg-white rounded-[3rem] p-12 lg:p-24 grid lg:grid-cols-12 gap-20 items-center shadow-xl shadow-primary/5 border border-primary/10">
              <div className="lg:col-span-5">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-secondary/10 rounded-[2rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="aspect-[4/5] rounded-[2rem] overflow-hidden relative border border-gray-200">
                    <img
                      alt="CEO Portrait"
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                      src={a.quote.image}
                    />
                  </div>
                  <div className={`absolute -bottom-8 ${isRTL ? '-left-8' : '-right-8'} bg-white/80 backdrop-blur-lg px-8 py-6 rounded-2xl border border-primary/10`}>
                    <p className={`text-primary ${isRTL ? 'font-cairo' : ''} text-2xl`}>
                      {a.quote.imageOverlayTitle}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {a.quote.imageOverlaySubtitle}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7 space-y-10">
                <MdFormatQuote className="text-6xl text-secondary/30" />
                <h2 className="text-3xl md:text-5xl font-black text-primary leading-tight">
                  {a.quote.text1}{' '}
                  <span className="text-secondary">{a.quote.textEmphasis}</span>
                </h2>
                <div className="space-y-6 text-lg text-slate-700 font-light leading-relaxed">
                  <p>{a.quote.desc1}</p>
                  <p>{a.quote.desc2}</p>
                </div>
                <div className="pt-4">
                  <img
                    alt="Saudi Vision 2030"
                    className="h-16 opacity-40 mb-6"
                    src={SaudiVisionLogo}
                  />
                  <p className="text-primary font-bold tracking-widest uppercase text-xs">
                    {a.quote.role}
                  </p>
                  <p className="text-secondary text-sm mt-1">{a.quote.company}</p>
                </div>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-40 bg-white">
          <div className="max-w-[1600px] mx-auto px-8 lg:px-16">
            <div className="text-center max-w-3xl mx-auto mb-24">
              <ScrollAnimation animation="fade-up">
                <h2 className="text-sm font-bold text-accent uppercase tracking-[0.5em] mb-6">
                  {a.advantage.label}
                </h2>
                <h3 className="text-4xl md:text-5xl lg:text-6xl font-black text-primary">
                  {a.advantage.title}
                </h3>
              </ScrollAnimation>
            </div>
            <ScrollAnimation animation="fade-up" delay={200} className="grid lg:grid-cols-3 gap-8">
              <div className="group p-12 rounded-[2.5rem] bg-surface border border-gray-100 hover:border-secondary/30 transition-all duration-500">
                <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mb-10 group-hover:bg-secondary/10 transition-colors">
                  <MdPublic className="text-3xl text-primary group-hover:text-secondary" />
                </div>
                <h4 className="text-2xl md:text-3xl text-primary mb-6">
                  {a.advantage.global.title}
                </h4>
                <p className="text-slate-600 text-lg leading-relaxed font-light">
                  {a.advantage.global.text}
                </p>
              </div>

              <div className="group p-12 rounded-[2.5rem] bg-surface border border-gray-100 hover:border-accent/30 transition-all duration-500">
                <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mb-10 group-hover:bg-accent/10 transition-colors">
                  <MdLocalShipping className="text-3xl text-primary group-hover:text-accent" />
                </div>
                <h4 className="text-2xl md:text-3xl text-primary mb-6">
                  {a.advantage.logistics.title}
                </h4>
                <p className="text-slate-600 text-lg leading-relaxed font-light">
                  {a.advantage.logistics.text}
                </p>
              </div>

              <div className="group p-12 rounded-[2.5rem] bg-surface border border-gray-100 hover:border-primary/30 transition-all duration-500">
                <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mb-10 group-hover:bg-primary/10 transition-colors">
                  <MdEngineering className="text-3xl text-primary" />
                </div>
                <h4 className="text-2xl md:text-3xl text-primary mb-6">
                  {a.advantage.technical.title}
                </h4>
                <p className="text-slate-600 text-lg leading-relaxed font-light">
                  {a.advantage.technical.text}
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="relative py-24 vision-banner-bg overflow-hidden border-y border-gray-100">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <svg
              className="h-full w-full"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              <path d="M0 100 L100 0 L100 100 Z" fill="white" />
            </svg>
          </div>
          <div className="max-w-[1600px] mx-auto px-8 lg:px-16 relative z-10">
            <ScrollAnimation animation="fade-right" className="flex flex-col lg:flex-row items-center justify-between gap-16">
              <div className="flex-1 space-y-8">
                <div className="flex items-center gap-4">
                  <span className="h-px w-12 bg-secondary" />
                  <span className="text-secondary text-xs font-bold tracking-[0.4em] uppercase">
                    {a.vision2030.label}
                  </span>
                </div>
                <h2 className="text-5xl lg:text-7xl font-black text-primary leading-[1.1]">
                  {a.vision2030.titleLine1}
                  <br />
                  <span className="text-secondary">{a.vision2030.titleEmphasis}</span>
                </h2>
                <p className="text-xl text-charcoal/70 font-light leading-relaxed max-w-2xl">
                  {a.vision2030.text}
                </p>
              </div>
              <div className="flex-shrink-0 lg:w-1/3 flex justify-center">
                <div className="relative p-12 bg-white rounded-[3rem] shadow-xl shadow-primary/5 border border-primary/5">
                  <img
                    alt="Saudi Vision 2030 Logo"
                    className="h-48 w-auto"
                    src={a.vision2030.logo || SaudiVisionLogo}
                  />
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-secondary rounded-full flex items-center justify-center shadow-lg">
                    <MdHighQuality className="text-white text-xl" />
                  </div>
                </div>
              </div>
            </ScrollAnimation>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
